import { useTranslation } from '@mukil/i18n';
import { Badge } from '@jumpstone/ui-react';
import {
  EMPLOYMENT_STATUS_KEY,
  EMPLOYMENT_STATUS_TONE,
} from '../../lib/label_keys';
import type { EmploymentStatus } from '../../data/types';

/**
 * The single canonical renderer for an employee's employment status
 * (active/on_leave/terminated) -- `EMPLOYMENT_STATUS_TONE`/`EMPLOYMENT_STATUS_KEY`
 * are the source-of-truth maps (`lib/label_keys.ts`); this component is the
 * only place that turns them into a `<Badge>`. Before this extraction the
 * same `<Badge variant={EMPLOYMENT_STATUS_TONE[status]}>` JSX was
 * independently duplicated across `employee_directory_screen.tsx` (desktop
 * row + phone card, #23603) and `employee_profile_screen.tsx` -- exactly
 * the drift risk `docs/design-system/CLAUDE.md`'s "One canonical component
 * per status/badge concept" section warns about: a future new status value
 * or tone change would otherwise need editing in three places to stay in
 * sync, and nothing would catch a missed one.
 */
export function EmploymentStatusBadge({
  status,
}: {
  status: EmploymentStatus;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <Badge variant={EMPLOYMENT_STATUS_TONE[status]}>
      {t(EMPLOYMENT_STATUS_KEY[status])}
    </Badge>
  );
}
